import type { Store } from 'vuex'
import type { Router } from 'vue-router'
import Swal from 'sweetalert2'
import { armForceLogoutWarning } from '@/composables/useForceLogout'
import { onHubEvent } from '@/composables/useConversationRealtime'
import { heartbeatSession, revokeSession } from '@/services/wootdashSessions'
import { decodeJwtPayload, decodeJwtSessionId } from '@/utils/jwt'
import { registerRenewedToken } from '@/api/wootdash'
import { renewWootDashToken } from '@/services/auth'
import { loadCurrentAgentRole } from '@/composables/useCurrentAgentRole'
import type { AuthUser } from '@/types/auth'

// Sessão desta aba no WootDash (substitui register/heartbeat/close_session do n8n). A sessão
// nasce no login (o backend grava e põe o id no JWT); aqui só batemos o ponto pra Admin › Sessões
// mostrar "visto agora". Sessão revogada = o backend recusa o token (401) → aviso e logout.
// Revogada pelo Sistema (Ctrl+Shift+S): o hub manda "SessaoRevogada" na hora e o aviso abre sem
// esperar o batimento.
const HEARTBEAT_MS = 60_000
/** O token vale Jwt:ExpirationMinutes (60): renova quando faltam menos que isto. */
const RENEW_BEFORE_MS = 15 * 60_000

interface SessionRevoked { sessionId: string; agentId: string; revokedBy: string; revokedAt: string }
/** Logout geral chegando (Configurações Gerais › Resolução e sessão): at = "22:00", minutes = quanto falta. */
interface ScheduledLogoutWarning { at: string; minutes: number }

let heartbeatTimer: ReturnType<typeof setInterval> | null = null
let currentToken = ''
let offRevoked: (() => void) | null = null
let offLogoutWarning: (() => void) | null = null
let onVisible: (() => void) | null = null

function expiresAt(token: string): number | null {
  const exp = decodeJwtPayload(token)?.exp
  return typeof exp === 'number' ? exp * 1000 : null
}

/** Token vencido pelo relógio daqui (o 401 foi expiração, não revogação). */
export function isTokenExpired(token: string): boolean {
  const at = expiresAt(token)
  return at !== null && at <= Date.now()
}

/** Troca o token em todo lugar: requisições (api/wootdash), store e o batimento. */
export function adoptNewToken(store: Store<any>, newToken: string): void {
  if (currentToken) registerRenewedToken(currentToken, newToken)
  currentToken = newToken
  store.commit('SET_TOKEN', newToken)
  // A regra geral (ver todas ou não) vem do token: relê já, sem esperar o próximo login.
  void loadCurrentAgentRole(newToken, '')
}

/** Faltando menos de 15 min pro token vencer (e ainda válido): pede um novo pra mesma sessão. */
async function renewIfNeeded(store: Store<any>): Promise<void> {
  const at = expiresAt(currentToken)
  if (at === null || at <= Date.now() || at - Date.now() > RENEW_BEFORE_MS) return
  const renewed = await renewWootDashToken(currentToken)
  if (renewed) adoptNewToken(store, renewed.token)
}

function showLogoutWarning(w: ScheduledLogoutWarning) {
  void Swal.fire({
    icon: 'warning',
    title: `Logout geral às ${w.at}`,
    html: `Você será desconectado em <b>${w.minutes} min</b>. Salve o que estiver fazendo.<br><small>Quem estiver com conversa em andamento no horário continua logado.</small>`,
    confirmButtonText: 'Entendi',
    confirmButtonColor: '#BC0C24',
  })
}

export async function startSessionHeartbeat(user: AuthUser, store: Store<any>, router: Router): Promise<void> {
  const token = user.wootdash_token
  if (!token) return
  currentToken = token

  offRevoked?.()
  offRevoked = onHubEvent('SessaoRevogada', payload => {
    const event = payload as SessionRevoked
    const sid = decodeJwtSessionId(currentToken)
    if (sid && event?.sessionId?.toLowerCase() === sid.toLowerCase()) armForceLogoutWarning('session', store, router, event.revokedBy)
  })
  offLogoutWarning?.()
  offLogoutWarning = onHubEvent('LogoutGeralAviso', payload => {
    const w = payload as ScheduledLogoutWarning
    if (w?.at) showLogoutWarning(w)
  })

  const beat = async () => {
    await renewIfNeeded(store)
    const status = await heartbeatSession(currentToken)
    // 401: token vencido (ficou parado/dormindo além da validade) ou sessão revogada — avisos diferentes.
    if (status === 401) armForceLogoutWarning(isTokenExpired(currentToken) ? 'expired' : 'session', store, router)
  }

  // Aba escondida/notebook dormindo atrasa o timer: ao voltar, confere (e renova) na hora.
  if (onVisible) document.removeEventListener('visibilitychange', onVisible)
  onVisible = () => { if (document.visibilityState === 'visible') void beat() }
  document.addEventListener('visibilitychange', onVisible)

  await beat()
  if (heartbeatTimer) return
  heartbeatTimer = setInterval(beat, HEARTBEAT_MS)
}

export function stopSessionHeartbeat(): void {
  if (heartbeatTimer) { clearInterval(heartbeatTimer); heartbeatTimer = null }
  if (onVisible) { document.removeEventListener('visibilitychange', onVisible); onVisible = null }
  offRevoked?.()
  offRevoked = null
  offLogoutWarning?.()
  offLogoutWarning = null
}

/** Chamado no logout (performLogout): encerra a própria sessão no WootDash. */
export async function closeCurrentSession(): Promise<void> {
  stopSessionHeartbeat()
  const token = currentToken
  currentToken = ''
  const sid = token ? decodeJwtSessionId(token) : null
  if (token && sid) await revokeSession(token, sid).catch(() => { /* o token expira sozinho */ })
}
